import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Search, XOctagon } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PnL, money, fmt, PageHeader, Table, Td } from "../../components/dashboard/Shared";
import { LiveBadge, ago, ConfirmModal } from "./mgrShared";

const TITLES = {
  active: ["Active Clients", "Clients with a connected MT5 account (live status shown per row)"],
  inactive: ["Inactive Clients", "Accounts not currently connected to MT5"],
  all: ["All Clients", "Every client who has connected an MT5 account"],
};

export default function ManagerClients({ segment = "all" }) {
  const [rows, setRows] = useState(null);
  const [brokers, setBrokers] = useState([]);
  const [search, setSearch] = useState("");
  const [broker, setBroker] = useState("");
  const [trading, setTrading] = useState("");
  const [busy, setBusy] = useState(false);
  const [manage, setManage] = useState(null); // account row being managed
  const [ovOn, setOvOn] = useState(false);
  const [ovLot, setOvLot] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [closingAll, setClosingAll] = useState(false);

  const openManage = (c) => { setManage(c); setOvOn(c.manager_override); setOvLot(c.manager_lot ?? c.client_lot ?? "0.01"); setReason(""); };
  const saveOverride = async () => {
    setSaving(true);
    try {
      await api.post(`/manager/mt5-clients/${manage.account_id}/lot-override`, { manager_override: ovOn, manager_lot: ovOn ? Number(ovLot) : undefined, reason });
      toast.success("Lot override updated"); setManage(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update override"); }
    setSaving(false);
  };
  const toggleTrading = async (c, enabled) => {
    try {
      await api.post(`/manager/mt5-clients/${c.account_id}/trading`, { enabled });
      toast.success(`ChartzFx trading ${enabled ? "enabled" : "disabled"}`); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/manager/mt5-clients", { params: { segment, search, broker, trading } });
    setRows(data.clients);
    setBrokers(data.brokers || []);
  }, [segment, search, broker, trading]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const refresh = async () => { setBusy(true); await load(); setBusy(false); };

  const closeAllGlobal = async () => {
    setClosingAll(true);
    try {
      const { data } = await api.post("/manager/close-all-positions");
      toast.success(`Closed ${data.closed} position${data.closed === 1 ? "" : "s"} across ${data.accounts} account${data.accounts === 1 ? "" : "s"}`);
      setConfirmAll(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to close positions"); }
    setClosingAll(false);
  };

  const [title, sub] = TITLES[segment] || TITLES.all;
  const openTotal = (rows || []).reduce((n, c) => n + (c.open_positions || 0), 0);

  return (
    <div>
      <PageHeader title={title} sub={sub}>
        {segment !== "inactive" && (
          <button data-testid="global-close-all-btn" onClick={() => setConfirmAll(true)} disabled={!openTotal}
            className="px-3 py-1.5 text-xs rounded-lg font-medium flex items-center gap-2 bg-[#FF3B30] text-white hover:bg-[#ff5147] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            <XOctagon size={13} /> Close All Trades{openTotal ? ` (${openTotal})` : ""}
          </button>
        )}
        <button data-testid="clients-refresh-btn" onClick={refresh} disabled={busy} className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2 disabled:opacity-50">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
        </button>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input data-testid="clients-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, email or MT5 login"
                 className="input-dark !w-64 !py-2 !pl-9 text-xs" />
        </div>
        <select data-testid="clients-broker-filter" value={broker} onChange={(e) => setBroker(e.target.value)} className="input-dark !w-40 !py-2 text-xs">
          <option value="">All brokers</option>
          {brokers.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select data-testid="clients-trading-filter" value={trading} onChange={(e) => setTrading(e.target.value)} className="input-dark !w-40 !py-2 text-xs">
          <option value="">Trading: All</option>
          <option value="on">Trading ON</option>
          <option value="paused">Trading Paused</option>
        </select>
      </div>

      <div className="card-dark p-4 sm:p-5">
        {!rows ? (
          <div className="skeleton h-32" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500 py-8 text-center">No clients match this view.</p>
        ) : (
          <div className="overflow-x-auto -mx-2 px-2">
            <Table headers={["Client", "Broker", "MT5", "Status", "Balance", "Equity", "Floating P&L", "Open", "Today (trades · P/L)", "Client Lot", "Override", "Active Lot", "Source", "ChartzFx", ""]} testId="mgr-clients-table">
              {rows.map((c) => (
                <tr key={c.account_id} data-testid={`mgr-client-row-${c.account_id}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                  <Td className="text-white"><Link data-testid={`client-link-${c.account_id}`} to={`/manager/client/${c.account_id}`} className="hover:text-[#00F0FF] transition-colors">{c.client_name}</Link><div className="text-[11px] text-gray-500">{c.client_email}</div></Td>
                  <Td className="text-xs">{c.broker}</Td>
                  <Td className="tabular text-xs">{c.login}<div className="text-[10px] text-gray-600">{c.server}</div></Td>
                  <Td><span title={c.error || undefined} data-testid={`row-status-${c.login}`}><LiveBadge state={c.connection_status === "active" ? c.data_state : c.connection_status === "engine_off" ? "engine_off" : "offline"} /></span><div className="text-[10px] text-gray-600" title={c.error || undefined}>{ago(c.last_seen)}{c.error ? " · sync error" : ""}</div></Td>
                  <Td className="tabular">{c.balance != null ? money(c.balance) : "—"}</Td>
                  <Td className="tabular text-[#00F0FF]">{c.equity != null ? money(c.equity) : "—"}</Td>
                  <Td>{c.floating_pnl != null ? <PnL value={c.floating_pnl} /> : <span className="text-gray-600 text-xs">N/A</span>}</Td>
                  <Td className="tabular">{c.open_positions ?? "—"}</Td>
                  <Td className="tabular text-xs" data-testid={`today-${c.account_id}`}>{c.today?.trades ?? 0} <span className="text-gray-600">·</span> {c.today ? <PnL value={c.today.net || 0} /> : "—"}</Td>
                  <Td className="tabular text-xs">{c.lot_mode === "risk" ? `${fmt(c.client_lot)} (risk)` : fmt(c.client_lot)}</Td>
                  <Td><span className={`text-[11px] ${c.manager_override ? "text-[#FFD600]" : "text-gray-500"}`}>{c.manager_override ? "ON" : "OFF"}</span></Td>
                  <Td className="tabular text-white" data-testid={`active-lot-${c.account_id}`}>{c.active_execution_lot != null ? fmt(c.active_execution_lot) : (c.lot_source === "CLIENT_RISK" ? "RISK%" : "—")}</Td>
                  <Td><span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-gray-300">{c.lot_source}</span></Td>
                  <Td>
                    <button data-testid={`trading-toggle-${c.account_id}`} onClick={() => toggleTrading(c, !c.chartzfx_trading_enabled)}
                      className={`text-[11px] px-2 py-1 rounded-full border ${c.chartzfx_trading_enabled ? "border-[#00E676]/40 text-[#00E676]" : "border-[#FF3B30]/40 text-[#FF3B30]"}`}>
                      {c.chartzfx_trading_enabled ? "ON" : "OFF"}
                    </button>
                  </Td>
                  <Td>
                    <div className="flex gap-3">
                      <Link data-testid={`details-btn-${c.account_id}`} to={`/manager/client/${c.account_id}`} className="text-[11px] text-white hover:underline">Details</Link>
                      <button data-testid={`manage-btn-${c.account_id}`} onClick={() => openManage(c)} className="text-[11px] text-[#00F0FF] hover:underline">Manage</button>
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
            <p className="text-[10px] text-gray-600 mt-3">Live values from MetaApi. "N/A" means the metric is not currently available from the broker.</p>
          </div>
        )}
      </div>

      {/* Manager controls modal */}
      {manage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="manage-modal" onClick={() => setManage(null)}>
          <div className="card-dark w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <p className="text-lg text-white mb-1">Lot & Trading Control</p>
            <p className="text-xs text-gray-500 mb-5">{manage.client_name} · {manage.broker} · {manage.login}</p>

            <div className="grid grid-cols-3 gap-3 mb-5 text-center">
              <div className="bg-white/[0.03] rounded-lg p-3"><p className="text-[10px] uppercase text-gray-500">Client Lot</p><p className="tabular text-white mt-1">{fmt(manage.client_lot)}</p></div>
              <div className="bg-white/[0.03] rounded-lg p-3"><p className="text-[10px] uppercase text-gray-500">Active Lot</p><p className="tabular text-[#00F0FF] mt-1">{manage.active_execution_lot != null ? fmt(manage.active_execution_lot) : (manage.lot_source === "CLIENT_RISK" ? "RISK%" : "—")}</p></div>
              <div className="bg-white/[0.03] rounded-lg p-3"><p className="text-[10px] uppercase text-gray-500">Source</p><p className="text-white mt-1 text-xs">{manage.lot_source}</p></div>
            </div>

            <label className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-200">Manager Override</span>
              <button data-testid="modal-override-toggle" onClick={() => setOvOn(!ovOn)} className={`w-11 h-6 rounded-full transition-colors ${ovOn ? "bg-[#7000FF]" : "bg-white/10"}`}>
                <span className={`block w-5 h-5 bg-white rounded-full transition-transform ${ovOn ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </label>
            {ovOn && (
              <div className="mb-3">
                <label className="label-xs">Manager Lot</label>
                <input data-testid="modal-manager-lot" type="number" step="0.01" min="0.01" value={ovLot} onChange={(e) => setOvLot(e.target.value)} className="input-dark !py-2 text-sm mt-1" />
              </div>
            )}
            <div className="mb-4">
              <label className="label-xs">Reason (optional)</label>
              <input data-testid="modal-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Emergency risk control" className="input-dark !py-2 text-sm mt-1" />
            </div>

            <div className="flex items-center justify-between mb-5 py-2 border-t border-white/5 pt-4">
              <span className="text-sm text-gray-200">ChartzFx Trading</span>
              <button data-testid="modal-trading-toggle" onClick={() => toggleTrading(manage, !manage.chartzfx_trading_enabled).then(() => setManage({ ...manage, chartzfx_trading_enabled: !manage.chartzfx_trading_enabled }))}
                className={`text-xs px-3 py-1.5 rounded-full border ${manage.chartzfx_trading_enabled ? "border-[#00E676]/40 text-[#00E676]" : "border-[#FF3B30]/40 text-[#FF3B30]"}`}>
                {manage.chartzfx_trading_enabled ? "ON — new trades allowed" : "OFF — new trades blocked"}
              </button>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setManage(null)} className="btn-outline flex-1 py-2.5 text-sm">Cancel</button>
              <button data-testid="modal-save-override" onClick={saveOverride} disabled={saving} className="btn-primary flex-1 py-2.5 text-sm disabled:opacity-50">{saving ? "Saving…" : "Save Override"}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirmAll}
        title="Close ALL open trades?"
        body={`This immediately closes every open position (${openTotal}) across all connected client accounts at market price. This affects real money and cannot be undone.`}
        confirmLabel={`Close All (${openTotal})`}
        busy={closingAll}
        onCancel={() => setConfirmAll(false)}
        onConfirm={closeAllGlobal}
      />
    </div>
  );
}
